import { PulseEnvironment, RouteHelperService, WebToMobileService } from '@pulse/browser-shared';
import { AnalyticsPlugin, TrackerConfigurations } from '@pulse/common';
import { DebugService } from '../services/debug.service';
import { LiveEventDebugService } from '../services/live-event-debug.service';

/**
 * Adds default state items such as app, device_id and session_id to state
 */
export class DebugEventPlugin implements AnalyticsPlugin {
    private static readonly DEBUGGER_KEY = 'debugger' as const;

    constructor(
        private readonly debugService: DebugService,
        private readonly env: PulseEnvironment,
        private readonly liveEventDebug: LiveEventDebugService,
        private readonly config: TrackerConfigurations,
        private readonly routeHelper: RouteHelperService,
        private readonly webToMobileService: WebToMobileService,
    ) {}

    /**
     * Initializes the debug event plugin by checking for debug parameters
     * and enabling appropriate debug modes.
     */
    public init(): void {
        try {
            const debugLEUID = this.extractDebugLEUID();

            if (debugLEUID) {
                this.enableDebugMode();
            }

            const isLiveDebugEnabled = this.isLiveDebugEnabled(debugLEUID);

            if (isLiveDebugEnabled) {
                this.handleLiveDebugMode();
            }

            if (debugLEUID) {
                this.cleanupDebugParameter();
            }
        } catch (error) {
            console.error('Failed to initialize DebugEventPlugin:', error);
        }
    }

    /**
     * Extracts the debug Live Event UID from the landing URL.
     */
    private extractDebugLEUID(): string | null {
        try {
            const url = new URL(this.env.landingUrl);

            return url.searchParams.get(this.config.debugLiveEventKey);
        } catch (error) {
            console.error('Failed to parse landing URL:', error);

            return null;
        }
    }

    /**
     * Enables debug mode for the live event key.
     */
    private enableDebugMode(): void {
        this.debugService.enable(this.config.debugLiveEventKey);
    }

    /**
     * Determines if live debug mode should be enabled.
     */
    private isLiveDebugEnabled(debugLEUID: string | null): boolean {
        return Boolean(debugLEUID) || this.debugService.enabled(this.config.debugLiveEventKey);
    }

    /**
     * Handles the activation of live debug mode including UI display.
     */
    private handleLiveDebugMode(): void {
        this.debugService.enable(DebugEventPlugin.DEBUGGER_KEY);

        const webToMobileMetaInfo = this.webToMobileService.getWebToMobileMetaInfo();
        const hasSessionId = Boolean(webToMobileMetaInfo?.['session_id']);

        if (!hasSessionId) {
            this.liveEventDebug.displayControl();
        }
    }

    /**
     * Removes the debug parameter from the URL.
     */
    private cleanupDebugParameter(): void {
        this.routeHelper.cleanupURL(this.config.debugLiveEventKey);
    }
}
